import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { CalendarClock, CalendarPlus, ChevronLeft, ChevronRight, ClipboardCheck, ClipboardList, Copy, FilePen, Gavel, Send, UserRoundX, Users, XCircle } from 'lucide-react';
import { useState, type MouseEvent, type ReactNode } from 'react';
import {
  BOOKED_INTERVIEW_STATUSES,
  INTERVIEW_STATUS_LABELS,
  PANELIST_ROLE_LABELS,
  RESCHEDULE_INITIATOR_LABELS,
  UNBOOKED_INTERVIEW_STATUSES,
  cancelReasonLabel,
  type InterviewView,
} from '@nest-recruit/shared';
import { Avatar } from '../../../components/Avatar';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Chip } from '../../../components/Chip';
import { Field } from '../../../components/Field';
import { Input } from '../../../components/Input';
import { Popover } from '../../../components/Popover';
import { Select } from '../../../components/Select';
import { SkeletonLines } from '../../../components/Skeleton';
import { INTERVIEW_STATUS_TONES } from '../../../components/status-tones';
import { ToneChip } from '../../../components/Tone';
import { ForbiddenState, NotFoundState, ServerErrorState } from '../../../components/system-states/SystemState';
import { anchorFrom, type AnchorRect } from '../../../lib/anchored-panel';
import { EMPTY, formatDateTime, formatPhone } from '../../../lib/format';
import { useNow } from '../../../lib/use-now';
import { apiPost, errorMessage } from '../../../lib/api-client';
import { useRoom } from '../../../shell/SocketProvider';
import { useToasts } from '../../../stores/toasts';
import { useCanView, useCurrentSession } from '../../../stores/session';
import { useInterview, useInterviews, useScheduler } from '../api/interviews-api';
import { InterviewsHeader, type InterviewsPage } from '../components/InterviewsHeader';
import { CancelPanel, CantMakeItPanel, OutcomePanel, ReschedulePanel, SwapPanel } from '../components/InterviewPanels';
import { useInterviewActions, useViewerZone } from '../hooks/use-interview-actions';
import { dayKey, placeLine, roundLine } from '../interview-ui';
import { listPosition, orderedRows, type InterviewListContext } from '../interview-list-context';
import styles from '../components/Interviews.module.css';
import { goneId, useRecordVisit } from '../../../lib/use-record-visit';

type PanelKind = 'move' | 'swap' | 'cancel' | 'outcome' | 'cant';

/**
 * SCH-04 — one interview: when, where, who, and its history. Where N-29–N-35 and N-77
 * land. The recruiter schedules and moves it from here; a panelist records it completed
 * or says they can't make it; a hiring manager reads it.
 */
export function InterviewPage({ interviewId }: { readonly interviewId: string }): ReactNode {
  const query = useInterview(interviewId);
  const actions = useInterviewActions(interviewId);
  const navigate = useNavigate();
  const zone = useViewerZone();
  const now = useNow();
  const userId = useCurrentSession()?.id;
  const canSeeDecisions = useCanView('stage_decision');
  const [panel, setPanel] = useState<{ readonly kind: PanelKind; readonly anchor: AnchorRect } | null>(null);
  const listContext = useRouterState({ select: (state) => state.location.state.interviewList });
  const view = query.data;
  useRecordVisit(view ? { kind: 'interview', id: interviewId, title: view.candidateName, subtitle: `${view.roundName} · ${view.jobTitle}`, path: `/interviews/${interviewId}` } : null, goneId(query.error, interviewId));
  useRoom(view?.can.manage ? `job:${view.jobId}` : null);

  if (query.isPending) return <SkeletonLines count={10} />;
  if (query.error) {
    if (query.error.status === 403) return <ForbiddenState resource="this interview" contact="The recruiter for this job" />;
    if (query.error.status === 404) return <NotFoundState onGoBack={() => void navigate({ to: '/interviews' })} />;
    return <ServerErrorState onRetry={() => void query.refetch()} />;
  }
  const interview = query.data;
  const isBooked = BOOKED_INTERVIEW_STATUSES.includes(interview.status);
  const isUnbooked = UNBOOKED_INTERVIEW_STATUSES.includes(interview.status);
  const hasStarted = interview.startsAt !== null && new Date(interview.startsAt).getTime() <= now;
  const isOnPanel = interview.panel.some((member) => member.userId === userId);
  const isHeld = interview.status !== 'cancelled' && interview.status !== 'candidate_no_show';
  const open = (kind: PanelKind) => (event: MouseEvent<HTMLButtonElement>): void => setPanel({ kind, anchor: anchorFrom(event.currentTarget) });
  const close = (): void => setPanel(null);
  const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Dhaka' }).format(now);

  return (
    <div className={styles.screen}>
      <InterviewsHeader current={headerTab(listContext)} />
      <div className={styles.detailNav}>
        <Link to={interview.can.manage ? '/interviews' : '/interviews/mine'} className={styles.back}>
          ← Interviews
        </Link>
        {listContext && <ListSteps interviewId={interviewId} context={listContext} zone={zone} />}
      </div>

      <Card
        leading={<Avatar name={interview.candidateName} size="lg" />}
        title={`${interview.candidateName} — ${interview.roundName}`}
        meta={`${interview.jobTitle} · ${interview.ventureName}`}
      >
        <div className={styles.actionsRow}>
          <ToneChip tone={INTERVIEW_STATUS_TONES[interview.status]}>{INTERVIEW_STATUS_LABELS[interview.status]}</ToneChip>
          {interview.rescheduleCount > 0 && <Chip tone="neutral">Moved {interview.rescheduleCount}×</Chip>}
        </div>
        {interview.isStranded && (
          <p className={styles.warning} role="status">
            {interview.candidateName} has left this interview stage, but the interview is still booked. Cancel it, or keep it if the move was a mistake.
          </p>
        )}
        {interview.candidateNoShows >= 2 && interview.can.manage && (
          <p className={styles.warning}>This candidate has missed {interview.candidateNoShows} interviews. Consider a cooldown before they apply again.</p>
        )}
        <Facts interview={interview} zone={zone} />
      </Card>

      <div className={styles.actionsRow}>
        {isOnPanel && isHeld && (
          <Button variant="solid" icon={FilePen} onClick={() => void navigate({ to: '/interviews/$interviewId/scorecard', params: { interviewId } })}>
            Open scorecard
          </Button>
        )}
        {!isOnPanel && (
          <Button variant="outline" icon={ClipboardList} onClick={() => void navigate({ to: '/interviews/$interviewId/scorecards', params: { interviewId } })}>
            Scorecards
          </Button>
        )}
        {/* S21: the round's decision is made on its debrief (DEC-01), once the interview was held. */}
        {canSeeDecisions && (interview.status === 'completed' || interview.status === 'panelist_no_show') && (
          <Button variant="outline" icon={Gavel} onClick={() => void navigate({ to: '/interviews/$interviewId/debrief', params: { interviewId } })}>
            Debrief
          </Button>
        )}
        {interview.can.manage && isUnbooked && (
          <Button variant="solid" icon={CalendarPlus} onClick={() => void navigate({ to: '/interviews/$interviewId/schedule', params: { interviewId } })}>
            {interview.status === 'slots_offered' ? 'Change the times' : 'Schedule'}
          </Button>
        )}
        {(interview.can.manage || interview.can.recordCompleted) && isBooked && hasStarted && (
          <Button variant="solid" icon={ClipboardCheck} onClick={open('outcome')}>
            How did it go?
          </Button>
        )}
        {interview.can.manage && isBooked && (
          <Button variant="outline" icon={CalendarClock} onClick={open('move')}>
            Move
          </Button>
        )}
        {interview.can.manage && (isBooked || isUnbooked) && (
          <Button variant="ghost" icon={Users} onClick={open('swap')}>
            Swap a panelist
          </Button>
        )}
        {interview.can.sayCantMakeIt && !interview.can.manage && (
          <Button variant="outline" icon={UserRoundX} onClick={open('cant')}>
            I can't make it
          </Button>
        )}
        {interview.can.manage && interview.status !== 'unscheduled' && (
          <Button variant="ghost" icon={Copy} onClick={() => void actions.copyMessage()}>
            Copy message
          </Button>
        )}
        {interview.can.manage && (isBooked || isUnbooked) && (
          <Button variant="ghost" icon={XCircle} onClick={open('cancel')}>
            Cancel
          </Button>
        )}
      </div>

      <div className={styles.columns}>
        <Card title="History" meta="Every change, newest first">
          {interview.history.length === 0 ? (
            <p className={styles.quiet}>Nothing yet.</p>
          ) : (
            <ul className={styles.list}>
              {interview.history.map((row, index) => (
                // Two changes can land in the same instant with the same kind (a swap writes two lines).
                <li key={`${row.at}-${row.eventType}-${index}`} className={styles.item}>
                  <span className={styles.meta}>{row.text}</span>
                  <span className={styles.quiet}>
                    {formatDateTime(row.at, { timeZone: zone })} · {row.actorName ?? 'the system'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <div className={styles.side}>
          <Card title="Panel">
            <ul className={styles.list}>
              {interview.panel.map((member) => (
                <li key={member.userId} className={styles.item}>
                  <div className={styles.itemHead}>
                    <span className={styles.person}>
                      <Avatar name={member.fullName} size="sm" />
                      <span className={styles.itemName}>{member.fullName}</span>
                    </span>
                    <Chip tone="neutral" plain>
                      {PANELIST_ROLE_LABELS[member.role]}
                    </Chip>
                    {member.didAttend === false && <ToneChip tone={INTERVIEW_STATUS_TONES.panelist_no_show}>Did not come</ToneChip>}
                  </div>
                  {member.substitutedForName && <span className={styles.quiet}>Standing in for {member.substitutedForName}</span>}
                  {interview.can.manage && (isBooked || interview.status === 'completed') && <SendLinkAgain interviewId={interview.id} userId={member.userId} name={member.fullName} />}
                </li>
              ))}
            </ul>
          </Card>
          {interview.can.manage && (isBooked || isUnbooked) && <WhereCard interview={interview} actions={actions} />}
        </div>
      </div>

      <Popover anchor={panel?.anchor ?? null} onClose={close} label="Interview action">
        {panel?.kind === 'move' && (
          <ReschedulePanel
            interview={interview}
            actions={actions}
            today={today}
            onCancel={close}
            onMoved={(hasNewTime) => {
              close();
              if (!hasNewTime) void navigate({ to: '/interviews/$interviewId/schedule', params: { interviewId } });
            }}
          />
        )}
        {panel?.kind === 'swap' && <SwapPanel interview={interview} actions={actions} onDone={close} onCancel={close} />}
        {panel?.kind === 'cancel' && <CancelPanel interview={interview} actions={actions} onDone={close} onCancel={close} />}
        {panel?.kind === 'outcome' && <OutcomePanel interview={interview} actions={actions} onDone={close} onCancel={close} />}
        {panel?.kind === 'cant' && <CantMakeItPanel interview={interview} actions={actions} onDone={close} onCancel={close} />}
      </Popover>
    </div>
  );
}

function Facts({ interview, zone }: { readonly interview: InterviewView; readonly zone: string }): ReactNode {
  const confirmation = interview.confirmedAt
    ? 'Confirmed'
    : BOOKED_INTERVIEW_STATUSES.includes(interview.status)
      ? 'Waiting for the candidate — confirms itself a day before'
      : EMPTY;
  return (
    <dl className={styles.facts}>
      <dt>When</dt>
      <dd>{interview.startsAt ? formatDateTime(interview.startsAt, { timeZone: zone }) : 'Not booked yet'}</dd>
      <dt>Round</dt>
      <dd>{roundLine(interview)}</dd>
      <dt>Where</dt>
      <dd>
        {interview.mode === 'online' && interview.meetingLink ? (
          <a href={interview.meetingLink} target="_blank" rel="noreferrer" className={styles.link}>
            {interview.meetingLink}
          </a>
        ) : (
          placeLine(interview)
        )}
      </dd>
      <dt>Candidate</dt>
      <dd>
        {interview.candidateEmail ?? EMPTY} · {interview.candidatePhone ? formatPhone(interview.candidatePhone) : EMPTY}
      </dd>
      <dt>Attending</dt>
      <dd>{confirmation}</dd>
      {interview.status === 'slots_offered' && (
        <>
          <dt>Times sent</dt>
          <dd>
            {interview.offeredSlots.length} · {interview.slotsOfferedAt ? formatDateTime(interview.slotsOfferedAt, { timeZone: zone }) : EMPTY}
          </dd>
        </>
      )}
      {interview.lastRescheduleInitiator && (
        <>
          <dt>Last moved</dt>
          <dd>{RESCHEDULE_INITIATOR_LABELS[interview.lastRescheduleInitiator]}</dd>
        </>
      )}
      {interview.scorecardDueAt && (
        <>
          <dt>Scorecards due</dt>
          <dd>{formatDateTime(interview.scorecardDueAt, { timeZone: zone })}</dd>
        </>
      )}
      {interview.cancelReasonCode && (
        <>
          <dt>Cancelled</dt>
          <dd>
            {cancelReasonLabel(interview.cancelReasonCode)}
            {interview.statusReason ? ` — ${interview.statusReason}` : ''}
          </dd>
        </>
      )}
    </dl>
  );
}

/** F10.13 / F10.14 — the link for online, the room for in person. */
function WhereCard({ interview, actions }: { readonly interview: InterviewView; readonly actions: ReturnType<typeof useInterviewActions> }): ReactNode {
  const scheduler = useScheduler(interview.id);
  const [link, setLink] = useState(interview.meetingLink ?? '');
  if (interview.mode === 'phone') return null;
  if (interview.mode === 'online') {
    const trimmed = link.trim();
    return (
      <Card title="Meeting link" meta="Create the meeting in your usual tool and paste the link">
        <Field label="Link">
          {({ inputId }) => <Input id={inputId} value={link} placeholder="https://meet.google.com/…" onChange={(event) => setLink(event.target.value)} />}
        </Field>
        <div className={styles.actionsRow}>
          <Button variant="solid" size="sm" disabled={trimmed === (interview.meetingLink ?? '')} onClick={() => void actions.details(interview, { meetingLink: trimmed === '' ? null : trimmed })}>
            Save link
          </Button>
          {BOOKED_INTERVIEW_STATUSES.includes(interview.status) && <span className={styles.quiet}>The candidate is sent it.</span>}
        </div>
      </Card>
    );
  }
  const rooms = scheduler.data?.rooms ?? [];
  return (
    <Card title="Room" meta={rooms.length === 0 ? 'No rooms yet — HR adds them under Admin → Rooms' : 'Checked against other interviews in the room'}>
      <Field label="Room">
        {({ inputId }) => (
          <Select
            id={inputId}
            label="Room"
            value={interview.roomId}
            placeholder="Choose a room"
            disabled={rooms.length === 0}
            options={rooms.map((room) => ({ value: room.id, label: room.name }))}
            // The room's name changes with it, so the card never shows the old room for a moment.
            onChange={(roomId) => void actions.details(interview, { roomId }, { roomName: rooms.find((room) => room.id === roomId)?.name ?? null })}
          />
        )}
      </Field>
    </Card>
  );
}

/** The tab the header marks: the list the interview was opened from, else the calendar. */
function headerTab(context: InterviewListContext | undefined): InterviewsPage {
  if (context?.filter.view === 'mine') return 'mine';
  if (context?.filter.view === 'to_schedule') return 'to_schedule';
  return 'calendar';
}

/**
 * Previous / Next through the list this interview was opened from, read from that list's cache —
 * never fetched for this, so with nothing cached (or this interview no longer in it) they hide.
 */
function ListSteps({ interviewId, context, zone }: { readonly interviewId: string; readonly context: InterviewListContext; readonly zone: string }): ReactNode {
  const navigate = useNavigate();
  const list = useInterviews(context.filter, false);
  const position = list.data ? listPosition(orderedRows(list.data, context, (iso) => dayKey(iso, zone)), interviewId) : null;
  if (!position || position.total < 2) return null;
  const go = (id: string): void => void navigate({ to: '/interviews/$interviewId', params: { interviewId: id }, state: { interviewList: context } });
  const { previous, next } = position;
  return (
    <span className={styles.detailPosition}>
      <Button variant="ghost" size="sm" icon={ChevronLeft} aria-label={previous ? `Previous interview: ${previous.candidateName}` : 'Previous interview'} disabled={!previous} onClick={() => previous && go(previous.id)} />
      {position.index + 1} of {position.total} {context.label}
      <Button variant="ghost" size="sm" icon={ChevronRight} aria-label={next ? `Next interview: ${next.candidateName}` : 'Next interview'} disabled={!next} onClick={() => next && go(next.id)} />
    </span>
  );
}

/** C6 (F11.12): a panelist's scorecard link again — it replaces the last one, which stops working. */
function SendLinkAgain({ interviewId, userId, name }: { readonly interviewId: string; readonly userId: string; readonly name: string }): ReactNode {
  const show = useToasts((toasts) => toasts.show);
  const [isSending, setIsSending] = useState(false);
  async function send(): Promise<void> {
    setIsSending(true);
    try {
      await apiPost<{ readonly sent: number }>(`/interviews/${interviewId}/scorecard-links`, { userId });
      show({ tone: 'success', message: `Scorecard link sent to ${name}`, detail: 'The link they had before no longer works.' });
    } catch (error) {
      show({ tone: 'danger', message: "The link wasn't sent", detail: errorMessage(error) });
    } finally {
      setIsSending(false);
    }
  }
  return (
    <Button variant="ghost" size="sm" icon={Send} isPending={isSending} onClick={() => void send()}>
      Send their scorecard link again
    </Button>
  );
}
